import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import * as Device from 'expo-device';
import * as Location from 'expo-location';
import { Platform } from 'react-native';

import { apiService } from './api-service';
import { autoCheckinService } from './auto-checkin-service';

import { User, LocationData } from '@/types/models';
import { logger } from '@/utils/logger';


// Storage keys
const LAST_LOCATION_KEY = 'lastKnownLocation';
const ANALYTICS_CONSENT_KEY = 'analyticsConsent';

export interface LocationTrackingStatus {
	isEnabled: boolean;
	hasPermission: boolean;
	lastUpdate?: Date;
	error?: string;
}

class LocationService {
	private isTracking = false;
	private foregroundSubscription: Location.LocationSubscription | null = null;
	private user: User | null = null;

	async initialize(user: User) {
		this.user = user;
		// Auto-start tracking when user is initialized
		await this.startTracking();
	}

	/**
	 * Request location permissions
	 */
	async requestPermissions(): Promise<boolean> {
		try {
			const { status } = await Location.requestForegroundPermissionsAsync();
			if (status !== 'granted') {
				logger.log('Foreground location permission denied');
				return false;
			}

			return true;
		} catch (error) {
			console.error('Error requesting location permissions:', error);
			return false;
		}
	}

	async hasAlwaysLocationPermission(): Promise<boolean> {
		// Background permissions are no longer requested; treat as false
		return false;
	}

	/**
	 * Start location tracking
	 */
	async startTracking(): Promise<boolean> {
		if (!this.user) {
			console.error('User not initialized');
			return false;
		}

		const hasPermission = await this.requestPermissions();
		if (!hasPermission) {
			return false;
		}

		try {
			// Start foreground location tracking
			this.foregroundSubscription = await Location.watchPositionAsync(
				{
					accuracy: Location.Accuracy.Balanced,
					timeInterval: 60000, // 1 minute
					distanceInterval: 50, // 50 meters
				},
				async (location) => {
					await this.handleLocationUpdate(location);
				},
			);

			this.isTracking = true;

			logger.log('Location tracking started for user:', this.user.id);
			return true;

		} catch (error) {
			console.error('Error starting location tracking:', error);
			return false;
		}
	}

	/**
	 * Stop location tracking
	 */
	async stopTracking(): Promise<void> {
		try {
			// Stop foreground tracking
			if (this.foregroundSubscription) {
				this.foregroundSubscription.remove();
				this.foregroundSubscription = null;
			}



			this.isTracking = false;

			logger.log('Location tracking stopped');

		} catch (error) {
			console.error('Error stopping location tracking:', error);
		}
	}

	/**
	 * Get current location
	 */
	async getCurrentLocation(): Promise<LocationData | null> {
		try {
			const hasPermission = await this.requestPermissions();
			if (!hasPermission) {
				return null;
			}

			const location = await Location.getCurrentPositionAsync({
				accuracy: Location.Accuracy.Balanced,
			});

			const locationData: LocationData = {
				latitude: location.coords.latitude,
				longitude: location.coords.longitude,
				accuracy: location.coords.accuracy || 0,
				timestamp: location.timestamp,
			};

			// Store as last known location
			await AsyncStorage.setItem(LAST_LOCATION_KEY, JSON.stringify(locationData));

			return locationData;

		} catch (error) {
			if (this.isLocationUnavailable(error)) {
				console.warn('Location unavailable, skipping current location fetch');
				return null;
			}
			console.error('Error getting current location:', error);
			return null;
		}
	}


	async syncCurrentLocationToBackend(): Promise<boolean> {
		try {
			const location = await Location.getCurrentPositionAsync({
				accuracy: Location.Accuracy.Balanced,
			});
			if (!location) {
				return false;
			}
			await this.handleLocationUpdate(location);
			return true;
		} catch (error) {
			if (this.isLocationUnavailable(error)) {
				console.warn('Location unavailable, skipping location sync');
				return false;
			}
			console.error('Error syncing current location to backend:', error);
			return false;
		}
	}

	/**
	 * Get last known location from storage
	 */
	async getLastKnownLocation(): Promise<LocationData | null> {
		try {
			const stored = await AsyncStorage.getItem(LAST_LOCATION_KEY);
			if (stored) {
				return JSON.parse(stored);
			}
			return null;
		} catch (error) {
			console.error('Error getting last known location:', error);
			return null;
		}
	}

	/**
	 * Check if location tracking is enabled (always true if permissions granted)
	 */
	async isTrackingEnabled(): Promise<boolean> {
		try {
			const { status } = await Location.getForegroundPermissionsAsync();
			return status === 'granted';
		} catch (error) {
			console.error('Error checking tracking status:', error);
			return false;
		}
	}

	async getTrackingStatus(): Promise<LocationTrackingStatus> {
		const isEnabled = await this.isTrackingEnabled();
		const { status } = await Location.getForegroundPermissionsAsync();
		const hasPermission = status === 'granted';

		const lastKnownLocation = await this.getLastKnownLocation();

		return {
			isEnabled,
			hasPermission,
			lastUpdate: lastKnownLocation?.timestamp ? new Date(lastKnownLocation.timestamp) : undefined,
		};
	}

	/**
	 * Get current tracking status
	 */
	isCurrentlyTracking(): boolean {
		return this.isTracking;
	}

	/**
	 * Handle location updates
	 */
	private async handleLocationUpdate(location: Location.LocationObject): Promise<void> {
		if (!this.user) {
			console.error('User not initialized');
			return;
		}

		const user = this.user;
		try {
			const locationData: LocationData = {
				latitude: location.coords.latitude,
				longitude: location.coords.longitude,
				accuracy: location.coords.accuracy || 0,
				timestamp: location.timestamp,
			};

			// Store location locally
			await AsyncStorage.setItem(LAST_LOCATION_KEY, JSON.stringify(locationData));

			await this.sendLocationAnalytics(locationData);

			logger.log('Location updated for user:', user.id, locationData);

			// Push location update to auto-checkin service
			await autoCheckinService.processLocationUpdate(user, locationData);

		} catch (error) {
			console.error('Error handling location update:', error);
		}
	}

	/**
	 * Start background location task (removed)
	 */
	private async startBackgroundLocationTask(): Promise<void> { /* no-op */ }

	private async sendLocationAnalytics(locationData: LocationData): Promise<void> {
		try {
			const hasConsent = await this.hasAnalyticsConsent();
			if (!hasConsent) {
				return;
			}

			const metadata = {
				devicePlatform: Platform.OS,
				deviceOS: Device.osName ? `${Device.osName}${Device.osVersion ? ` ${Device.osVersion}` : ''}` : Platform.OS,
				deviceModel: Device.modelName || (Platform.OS === 'ios' ? 'iOS Device' : Platform.OS === 'android' ? 'Android Device' : 'unknown'),
				appVersion: (Constants as any)?.expoConfig?.version || 'unknown',
				isEmulator: Device.isDevice === false,
				accuracy: locationData.accuracy,
				timestamp: locationData.timestamp,
				source: 'location-service',
			};

			await apiService.fetchApi('/analytics', {
				method: 'POST',
				body: JSON.stringify({
					action: 'location_update',
					location: {
						latitude: locationData.latitude,
						longitude: locationData.longitude,
						accuracy: locationData.accuracy,
					},
					metadata,
				}),
			});
		} catch (error) {
			logger.log('Failed to record location analytics:', error);
		}
	}

	/**
	 * Clean up service
	 */
	async cleanup(): Promise<void> {
		await this.stopTracking();
	}

	private isLocationUnavailable(error: unknown): boolean {
		if (!error || typeof error !== 'object') return false;
		const candidate = error as { code?: string; message?: string };
		if (candidate.code === 'ERR_LOCATION_UNAVAILABLE') return true;
		return typeof candidate.message === 'string' && candidate.message.includes('ERR_LOCATION_UNAVAILABLE');
	}

	private async hasAnalyticsConsent(): Promise<boolean> {
		try {
			const stored = await AsyncStorage.getItem(ANALYTICS_CONSENT_KEY);
			if (stored === 'granted') return true;
			if (stored === 'denied') return false;
		} catch {
			return Platform.OS !== 'ios';
		}

		return Platform.OS !== 'ios';
	}
}

export const locationService = new LocationService();
